import React, { useEffect, useRef } from 'react'
import style from './style'
import Icon from '../Icon/Icon'
import Button, { ButtonType } from '../Button/Button'
import IconName from '../../helpers/types/IconName.interface'
import { makeStyles } from '@material-ui/core'
import { PropsWithChildren } from 'react'
import { AlignType } from '../Typography/Typography.interface'

const useStyles = makeStyles(style)

type AlertLevelPropType = 'success' | 'error' | 'warning'
interface PropsInterface {
    level?: AlertLevelPropType;
    onClose?(): any;
    buttonProps?: ButtonType;
    icon?: boolean | IconName;
    noBorderRadius?: boolean;
    autocloseAfter?: false | number; // ms
    className?: string;
    textAlign?: AlignType;
    marginY?: number; // px
    autoclose?: boolean; // autcloses after 5000ms if autocloseAfter not provided
    autoscroll?: boolean;
    withGradient?: boolean;
    'data-testid'?: string;
}

const icons = {
    error: {
        name: 'alert',
        color: 'white'
    },
    success: {
        name: 'copy',
        color: 'primary'
    },
    warning: {
        name: 'soft-alert-outline',
        color: 'primary2'
    }
}

const renderIcon = (icon, level, withGradient) => {
    if (icon === false) return null
    let iconName
    if (icon === true) {
        iconName = icons[level] ? icons[level].name : 'alert'
    } else {
        iconName = icon
    }

    const iconColor = withGradient ? 'neutral-900' : icons?.[level]?.color || 'white'

    return <Icon
        outlined
        name={iconName}
        color={iconColor}
        className='alert--icon'
    />
}

const renderButton = (buttonProps, level) => {
    if (!buttonProps) return null

    const { variant, color: buttonColor, className, size, ...restProps } = buttonProps
    const classes = ['alert--button', className].filter(Boolean).join(' ')
    const resolvedColor = buttonColor ?? (icons[level] ? icons[level].color : 'white')
    const resolvedVariant = variant ?? 'outlined'
    const resolvedSize = size ?? 'small'

    return <Button
        {...restProps}
        variant={resolvedVariant}
        color={resolvedColor}
        className={classes}
        size={resolvedSize}
    />
}

const renderCloseButton = (cb, level) => {
    if (!cb || (cb && typeof cb !== 'function')) {
        return null
    }

    return <Icon name='close' color={icons[level] ? icons[level].color : 'white'} className='alert--close-icon' onClick={cb} data-testid='close-alert-btn'/>
}

const isElementVisible = (element: HTMLDivElement) => {
    const { top, bottom } = element.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    return bottom > 0 && top < viewportHeight
}

/**
 *
 */
const Alert = ({
    level = 'error',
    onClose,
    buttonProps,
    icon = true,
    children,
    noBorderRadius = false,
    autocloseAfter = false,
    marginY,
    autoclose = false,
    autoscroll = false,
    className,
    textAlign = 'center',
    withGradient = false,
    'data-testid': dataTestId = 'alert'
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    if (!children) return <></>

    const styles = useStyles({ textAlign, withGradient })
    const alertRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!onClose) return

        const delay = typeof autocloseAfter === 'number' && autocloseAfter > 0
            ? autocloseAfter
            : autoclose
                ? 5000
                : null

        if (!delay) return

        const timeoutId = setTimeout(() => {
            onClose()
        }, delay)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [autoclose, autocloseAfter, onClose])

    useEffect(() => {
        if (!autoscroll || !alertRef.current || isElementVisible(alertRef.current)) {
            return
        }

        alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, [autoscroll])

    const classes = [level, noBorderRadius && 'no-border-radius'].filter((x) => x).map((x) => `alert--${x}`).join(' ')
    let inlineStyle
    if (marginY) {
        const pxs = `${marginY}px`
        inlineStyle = { marginTop: pxs, marginBottom: pxs }
    }
    return (
        <div ref={alertRef} className={`${styles.alert} ${classes} ${className || ''}`} style={inlineStyle} data-testid={dataTestId}>
            <div className='alert--content'>
                <div className='alert--content-inner'>
                    {renderIcon(icon, level)}
                    <span className='alert--message'>
                        {children}
                    </span>
                </div>
                {renderButton(buttonProps, level)}
            </div>
            {renderCloseButton(onClose, level)}
        </div>
    )
}

/**
 *
 */
export type AlertType = PropsWithChildren<PropsInterface>
/**
 *
 */
export type AlertLevelType = AlertLevelPropType

export default Alert
